/**
 * Amounts and prices travel as decimal strings so no precision is lost. Their
 * scale is not significant — Postgres returns zero as `0`, not `0.0000` — so
 * every display path parses and reformats rather than printing the raw string.
 */
export function parseAmount(value: string | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

/** Format a stock amount, dropping the trailing zeros the database carries. */
export function formatAmount(value: string | null | undefined, unit?: string | null): string {
  if (value === null || value === undefined) return '—'
  const amount = parseAmount(value)
  const text = amount.toLocaleString(undefined, { maximumFractionDigits: 4 })
  return unit ? `${text} ${unit}` : text
}

/**
 * Format a price in the base currency. Component prices are routinely fractions
 * of a cent, so small values keep more digits rather than rounding to 0.00.
 */
export function formatPrice(
  value: string | null | undefined,
  currency: string | undefined,
): string {
  if (value === null || value === undefined) return '—'
  const amount = parseAmount(value)
  const digits = amount !== 0 && Math.abs(amount) < 0.01 ? 5 : 2
  try {
    return amount.toLocaleString(undefined, {
      style: 'currency',
      currency: currency ?? 'EUR',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })
  } catch {
    // An unknown ISO code makes Intl throw; the number is still worth showing.
    return `${amount.toFixed(digits)} ${currency ?? ''}`.trim()
  }
}

/*
 * The exact arithmetic below keeps amounts as scaled BigInt (12 fraction
 * digits, past NUMERIC(16,4) amounts and NUMERIC(18,6) money) so no float
 * ever touches a value the server will compare against.
 */
const SCALE = 1_000_000_000_000n

function toScaled(value: string): bigint | null {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value.trim())
  if (!match || (match[3] !== undefined && match[3].length > 12)) return null
  const whole = BigInt(match[2]!) * SCALE
  const fraction = BigInt(match[3]?.padEnd(12, '0') ?? '0')
  const scaled = whole + fraction
  return match[1] === '-' ? -scaled : scaled
}

function fromScaled(scaled: bigint): string {
  const sign = scaled < 0n ? '-' : ''
  const abs = scaled < 0n ? -scaled : scaled
  const fraction = (abs % SCALE).toString().padStart(12, '0').replace(/0+$/, '')
  return `${sign}${abs / SCALE}${fraction ? `.${fraction}` : ''}`
}

/** The same value without the database's trailing zeros. */
export function trimDecimal(value: string): string | null {
  const scaled = toScaled(value)
  return scaled === null ? null : fromScaled(scaled)
}
/**
 * An amount a human just typed: comma or point as the separator, and the
 * bare forms (`.5`, `5.`) that never appear in database strings.
 */
export function typedAmount(value: string): string | null {
  const normalized = value.trim().replace(',', '.')
  return trimDecimal(normalized.replace(/^(-?)\.(\d+)/, '$10.$2').replace(/^(-?\d+)\.$/, '$1'))
}

export function addDecimal(a: string, b: string): string | null {
  const x = toScaled(a)
  const y = toScaled(b)
  return x === null || y === null ? null : fromScaled(x + y)
}

export function subDecimal(a: string, b: string): string | null {
  const x = toScaled(a)
  const y = toScaled(b)
  return x === null || y === null ? null : fromScaled(x - y)
}

/** `a × b ÷ c`, rounded toward negative infinity — "how many c-sized shares
 * fit in a lots of b each", without the two float divisions that drift. */
export function mulFloorDivDecimal(a: string, b: string, c: string): string | null {
  const x = toScaled(a)
  const y = toScaled(b)
  const z = toScaled(c)
  if (x === null || y === null || z === null || z === 0n) return null
  const product = (x * y) / SCALE
  const remainder = product % z
  return (remainder !== 0n && product < 0n !== z < 0n ? product / z - 1n : product / z).toString()
}
